// ===== Área de Clientes (módulo centralizado) =====
import React from 'react';
import { Icon } from './icons.jsx';
import { FilterBar } from './filterbar.jsx';
import { useStore } from './store.jsx';
import { ROLE_SCOPE, usePermission } from './auth.jsx';
import { Select } from './modals.jsx';
import { MenuDeCanal, MenuMaisFiltros } from './menu-de-filtro.jsx';
import { ListaDePrecos } from './lista-de-precos.jsx';
import { rotuloDaTabela } from './preco-tabela.js';
import { dataBr } from './data-br.js';
import {
  GMAIL_THREADS, NICHOS, NICHOS_BY_ID, STAGE_NAMES, TIPO_CLIENTE, TIPO_CLIENTE_BY_ID,
  brl, brlCompact, fiscalOk, recomendacaoParaCliente, waLink,
} from './data.jsx';

const { useState: useStateCli, useMemo: useMemoCli } = React;

function ClienteAvatar({ cliente, size = 32 }) {
  const tipo = TIPO_CLIENTE_BY_ID[cliente.tipo];
  const initials = cliente.nome.split(/[ ·]/).filter(Boolean).map(w => w[0]).slice(0, 2).join("").toUpperCase();
  // O ERP grava o CNPJ no lugar do nome em ~15 % dos clientes; a inicial seria um dígito solto ("6", "9").
  const nomeNumerico = /^\d/.test(cliente.nome.trim());
  return (
    <div className="cli-av" style={{ width: size, height: size, fontSize: size <= 28 ? 10 : 12, background: tipo?.tone || "var(--color-bg)", color: tipo?.color || "var(--color-ink)" }}>
      {nomeNumerico ? <Icon name="building" size={size <= 28 ? 12 : 16} /> : initials}
    </div>
  );
}

function TipoChip({ tipo, size = "md" }) {
  const t = typeof tipo === "string" ? TIPO_CLIENTE_BY_ID[tipo] : tipo;
  if (!t) return null;
  return (
    <span className={`tipo-chip sz-${size}`} style={{ background: t.tone, color: t.color }}>
      <span className="dot" style={{ background: t.color }} />
      {t.short}
    </span>
  );
}

// Motivos gravados pela carga em `clientes.reativacao_motivo`. Motivo fora desta lista não desenha selo.
const SELO_REATIVACAO = {
  inativo:  { label: "Reativar",          hint: "Inativo, mas comprou em 3 ou mais datas diferentes." },
  atrasado: { label: "Recompra atrasada", hint: "Comprava com regularidade e já passou de 1,5 vez o intervalo típico entre compras." },
};

// A data chega como "AAAA-MM-DD". `new Date("2026-01-01")` é meia-noite UTC e, no fuso do Brasil, mostraria 31/12: por isso é texto.
function dataBrDeTexto(iso) {
  const p = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return p ? `${p[3]}/${p[2]}/${p[1]}` : null;
}

function StatusCarteira({ status, reativacao }) {
  const map = {
    ativo:    { label: "Ativo",      cls: "ok",     ic: "check-circle-2", hint: "Comprou nos últimos 180 dias" },
    inativo:  { label: "Inativo",    cls: "muted",  ic: "clock",          hint: "Sem compras há mais de 180 dias (automático)" },
    prospec:  { label: "Prospecção", cls: "accent", ic: "sparkles",       hint: "Ainda não comprou" },
  };
  const m = map[status] || map.ativo;
  const chip = (
    <span className={`status-cart ${m.cls}`} title={m.hint}>
      <Icon name={m.ic} size={10} />
      {m.label}
    </span>
  );
  // `hasOwn`: um motivo como "constructor" ou "toString" achava uma função herdada de Object.prototype e o selo saía com "undefined".
  const selo = reativacao && Object.hasOwn(SELO_REATIVACAO, reativacao.motivo) ? SELO_REATIVACAO[reativacao.motivo] : null;
  if (!selo) return chip;
  const data = dataBrDeTexto(reativacao.em);
  return (
    <span className="status-cart-grupo">
      {chip}
      <span className="status-cart reativar" title={`Indicado para reativação${data ? ` em ${data}` : ""}. ${selo.hint}`}>
        <Icon name="refresh-cw" size={10} />
        {selo.label}
      </span>
    </span>
  );
}

function ClientesFiltros({ filters, setFilters, total, onNewCliente, clientes, canais, pipelineByCanal }) {
  // O menu "+" global ja escondia o cadastro de quem nao tem `clientes.create`
  // (shell.jsx); este botao nao. O closer clicava, preenchia tudo e o banco
  // recusava -- e a recusa era generica.
  const podeCriar = usePermission('clientes.create');
  const update = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const toggleIn = (k, v) => setFilters(f => {
    const arr = f[k] || [];
    return { ...f, [k]: arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v] };
  });
  const clearKey = (k) => setFilters(f => ({ ...f, [k]: [] }));
  // Um menu aberto por vez: abrir um fecha o outro.
  const [menu, setMenu] = useStateCli(null);
  // Cada tecla em `filters.q` refiltra e redesenha a lista; esperar 250 ms depois da última tecla junta "arq" numa só
  // renderização. `texto` é o que aparece no campo; `filters.q` só muda quando a espera acaba.
  const [texto, setTexto] = useStateCli(filters.q);
  const espera = React.useRef(null);
  const enviado = React.useRef(filters.q);
  const gravarBusca = (valor) => { enviado.current = valor; update("q", valor); };
  const digitar = (valor) => {
    setTexto(valor);
    clearTimeout(espera.current);
    espera.current = setTimeout(() => gravarBusca(valor), 250);
  };
  const limparBusca = () => {
    clearTimeout(espera.current);
    setTexto("");
    gravarBusca("");
  };
  React.useEffect(() => {
    // `filters.q` mudou por fora (drill-down via initialFilters): o campo acompanha e uma digitação pendente perde.
    if (filters.q === enviado.current) return;
    clearTimeout(espera.current);
    enviado.current = filters.q;
    setTexto(filters.q);
  }, [filters.q]);
  React.useEffect(() => () => clearTimeout(espera.current), []);
  const ufs = [...new Set(clientes.map(c => c.endereco?.uf).filter(Boolean))].sort();
  return (
    <FilterBar>
      <div className="seg multi">
        <button className={filters.tipo.length === 0 ? "on" : ""} onClick={() => clearKey("tipo")}>Todos</button>
        {TIPO_CLIENTE.map(t => (
          <button key={t.id} className={filters.tipo.includes(t.id) ? "on" : ""} onClick={() => toggleIn("tipo", t.id)}>
            <span style={{ width: 6, height: 6, background: t.color, borderRadius: 2, display: "inline-block" }} />
            {t.short}
          </button>
        ))}
      </div>
      <div className="seg multi">
        <button className={filters.status.length === 0 ? "on" : ""} onClick={() => clearKey("status")}>Status</button>
        <button className={filters.status.includes("ativo") ? "on" : ""} onClick={() => toggleIn("status", "ativo")}>Ativos</button>
        <button className={filters.status.includes("prospec") ? "on" : ""} onClick={() => toggleIn("status", "prospec")}>Prospec.</button>
        <button className={filters.status.includes("inativo") ? "on" : ""} onClick={() => toggleIn("status", "inativo")}>Inativos</button>
      </div>
      <div className="fb-search" style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 10px", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", height: 32, background: "var(--color-surface)" }}>
        <Icon name="search" size={13} style={{ color: "var(--color-ink-soft)" }} />
        <input
          placeholder="Buscar cliente, canal ou cidade..."
          value={texto}
          onChange={e => digitar(e.target.value)}
          style={{ border: 0, outline: "none", flex: 1, fontSize: 12.5, background: "transparent" }}
        />
        {texto && <button className="icon-btn" style={{ width: 22, height: 22 }} onClick={limparBusca}><Icon name="x" size={12} /></button>}
      </div>
      {/* Celular: os chips de sempre. No desktop a CSS os esconde e `fb-desk` assume (Canal e "Mais filtros" abrem menus). Os dois grupos
          leem e gravam o mesmo `filters`. `display: contents` mora na CSS, nao inline: um estilo inline venceria a regra que esconde o grupo. */}
      <span className="fb-mob">
        <button className={`chip-btn ${filters.canal.length ? "active" : ""}`} onClick={() => update("__canalOpen", !filters.__canalOpen)}>
          <Icon name="compass" size={13} /> Canal
          {filters.canal.length > 0 && <span style={{ background: "var(--color-accent)", color: "white", fontSize: 10, fontWeight: 700, padding: "0 5px", borderRadius: 999, marginLeft: 4 }}>{filters.canal.length}</span>}
        </button>
        <Select variant="chip" icon="package" multi chipLabel="Nicho" value={filters.nicho} onChange={v => update("nicho", v)}
          options={NICHOS.map(n => ({ value: n.id, label: n.label }))} />
        <Select variant="chip" icon="globe" multi chipLabel="UF" value={filters.uf} onChange={v => update("uf", v)}
          options={ufs.map(u => ({ value: u, label: u }))} />
        <button className={`chip-btn ${filters.alertas ? "active" : ""}`} onClick={() => update("alertas", !filters.alertas)} title="Somente clientes com alertas pendentes">
          <Icon name="bell" size={13} /> Com alertas
        </button>
        <button className={`chip-btn ${filters.reativar ? "active" : ""}`} onClick={() => update("reativar", !filters.reativar)} title="Somente clientes indicados para reativação">
          <Icon name="refresh-cw" size={13} /> Reativar
        </button>
      </span>
      {/* Depois de `fb-mob`: no jsdom (sem CSS) os dois grupos existem, e quem procura o primeiro botao "Canal" ou "Reativar" acha o chip do celular. */}
      <span className="fb-desk">
        <MenuDeCanal canais={canais} pipelineByCanal={pipelineByCanal} valor={filters.canal} onChange={v => update("canal", v)}
          aberto={menu === "canal"} onAbrir={() => setMenu("canal")} onFechar={() => setMenu(null)} />
        <MenuMaisFiltros ufs={ufs} filtros={filters} onNicho={v => toggleIn("nicho", v)} onUf={v => toggleIn("uf", v)}
          onAlertas={() => update("alertas", !filters.alertas)} onReativar={() => update("reativar", !filters.reativar)}
          onLimpar={() => setFilters(f => ({ ...f, nicho: [], uf: [], alertas: false, reativar: false }))}
          aberto={menu === "mais"} onAbrir={() => setMenu("mais")} onFechar={() => setMenu(null)} />
      </span>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 12, color: "var(--color-ink-soft)" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--color-ink)" }}>{total}</span> clientes
        </div>
        {podeCriar && <button className="btn primary" onClick={onNewCliente}><Icon name="plus" size={13} /> Novo cliente</button>}
      </div>
    </FilterBar>
  );
}

function CondPagamentoBadge({ cond }) {
  if (!cond) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, fontFamily: "var(--font-mono)", fontWeight: 600, background: "var(--color-bg)", border: "1px solid var(--color-border)", borderRadius: 4, padding: "1px 5px", color: "var(--color-ink-soft)" }}>
      <Icon name="credit-card" size={9} /> {cond}
    </span>
  );
}

function EmRiscoBadge() {
  return (
    <span title="Sem compras nos últimos 90 dias" style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, background: "var(--color-danger-soft, #fde8e8)", color: "var(--color-danger)", border: "1px solid var(--color-danger)", borderRadius: 4, padding: "1px 5px" }}>
      <Icon name="alert-triangle" size={9} /> Em risco
    </span>
  );
}

function ClienteRow({ cliente, onOpen, especificadores, pipeline, alertasPend, emRisco }) {
  const canal = cliente.canalId ? especificadores.find(e => e.id === cliente.canalId) : null;
  // Sem contato o texto antigo começava com "·" (47 % das linhas), e sem os dois sobrava só o "·".
  const subtitulo = [cliente.contato, cliente.cidade].filter(Boolean).join(" · ");
  return (
    <tr onClick={() => onOpen(cliente.id)} style={{ cursor: "pointer" }} data-screen-label={`Cliente row / ${cliente.nome}`}>
      <td className="cli-td-cliente">
        <div className="cli-cell">
          <ClienteAvatar cliente={cliente} />
          <div className="cli-cell-body">
            <div className="cli-cell-name">
              <span className="cli-nome-texto">{cliente.nome}</span>
              {cliente.isCanal && <span className="canal-badge" title="Também é canal de vendas"><Icon name="compass" size={9} /> Canal</span>}
              {emRisco && <EmRiscoBadge />}
            </div>
            {subtitulo && <div className="cli-cell-sub">{subtitulo}</div>}
          </div>
        </div>
      </td>
      <td className="cli-td-tipo"><TipoChip tipo={cliente.tipo} /></td>
      <td className="cli-td-status">
        <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
          <StatusCarteira status={cliente.status} reativacao={cliente.reativacao} />
          <CondPagamentoBadge cond={cliente.condPagamento} />
        </div>
      </td>
      <td className="cli-td-canal">
        {canal ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <Icon name="compass" size={11} style={{ color: "var(--color-cool)" }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{canal.nome}</div>
              <div style={{ fontSize: 10.5, color: "var(--color-ink-soft)" }}>{canal.pctRepasse}% repasse</div>
            </div>
          </div>
        ) : <span style={{ color: "var(--color-ink-faint)", fontSize: 12 }}>Sem canal</span>}
      </td>
      <td className="cli-td-fat">
        <div style={{ display: "flex", flexDirection: "column", gap: 1, fontSize: 12 }}>
          <div className="cli-fat-valor" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span className="mono" style={{ fontWeight: 700, color: "var(--color-ok)" }}>{brlCompact(cliente.faturamento)}</span>
            <span style={{ color: "var(--color-ink-faint)", fontSize: 11 }}>· {cliente.lastBuy}</span>
          </div>
          {pipeline > 0 && (
            <div className="mono cli-pipeline" style={{ fontSize: 10.5, color: "var(--color-accent-deep)", fontWeight: 600 }}>{brlCompact(pipeline)} em andamento</div>
          )}
        </div>
      </td>
      <td className="cli-td-neg">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span className="deal-pill mono" title={`${cliente.dealsAtivos} ativos · ${cliente.dealsTotal} total`}>
            <strong>{cliente.dealsAtivos}</strong>/{cliente.dealsTotal}
          </span>
        </div>
      </td>
      <td className={alertasPend > 0 ? "cli-td-alertas" : "cli-td-alertas cli-sem"}>
        {alertasPend > 0 ? (
          <span className="alert-pip" title={`${alertasPend} alertas pendentes`}><Icon name="bell" size={10} /> {alertasPend}</span>
        ) : <span style={{ color: "var(--color-ink-faint)", fontSize: 11 }}>—</span>}
      </td>
      <td className="cli-td-acao" style={{ textAlign: "right" }}>
        <button className="icon-btn" onClick={(e) => { e.stopPropagation(); onOpen(cliente.id); }}><Icon name="external-link" size={14} /></button>
      </td>
    </tr>
  );
}

function ClientesPage({ onOpenCliente, onOpenLead, onNewCliente, initialFilters }) {
  const store = useStore();
  const [filters, setFilters] = useStateCli({ tipo: [], status: [], canal: [], q: "", nicho: [], uf: [], alertas: false, reativar: false });
  // Desenhar os 2201 clientes de uma vez custava ~1,9 s para abrir e ~0,5 s por tecla (89 mil nós de DOM): a tabela mostra
  // `limite` linhas. Os totais e KPIs continuam sobre a lista inteira (`filtered`).
  const POR_VEZ = 50;
  const [limite, setLimite] = useStateCli(POR_VEZ);
  // Só o que filtra entra na chave: abrir o painel de canais (`__canalOpen`) não pode zerar o "Mostrar mais".
  const chaveDosFiltros = JSON.stringify([filters.tipo, filters.status, filters.canal, filters.nicho, filters.uf, filters.alertas, filters.reativar, filters.q]);
  const [chaveAnterior, setChaveAnterior] = useStateCli(chaveDosFiltros);
  if (chaveAnterior !== chaveDosFiltros) {
    // Ajuste durante a renderização (padrão documentado do React): evita um quadro com a lista nova ainda no limite antigo,
    // e volta a 50 mesmo que o filtro retorne ao valor anterior.
    setChaveAnterior(chaveDosFiltros);
    setLimite(POR_VEZ);
  }

  React.useEffect(() => {
    if (!initialFilters) return;
    // drill-down pode mandar valores únicos (string) — normaliza para array nos filtros múltiplos
    const toArr = (v) => v == null ? [] : (Array.isArray(v) ? v : [v]);
    const canalArr = toArr(initialFilters.canal);
    setFilters({
      tipo: toArr(initialFilters.tipo),
      status: toArr(initialFilters.status),
      nicho: toArr(initialFilters.nicho),
      uf: toArr(initialFilters.uf),
      canal: canalArr,
      q: initialFilters.q || "",
      alertas: !!initialFilters.alertas,
      reativar: !!initialFilters.reativar,
      __canalOpen: canalArr.length > 0,
    });
  }, [initialFilters]);

  // Faturamento em andamento (pipeline aberto) por cliente e por canal
  const pipelineByCliente = useMemoCli(() => {
    const m = {};
    store.leads.forEach(l => {
      if (l.perdido || l.stage === "GANHO" || !l.clienteId) return;
      m[l.clienteId] = (m[l.clienteId] || 0) + (l.valor || 0);
    });
    return m;
  }, [store.leads]);
  const pipelineByCanal = useMemoCli(() => {
    const m = {};
    store.leads.forEach(l => {
      if (l.perdido || l.stage === "GANHO" || !l.especificadorId) return;
      m[l.especificadorId] = (m[l.especificadorId] || 0) + (l.valor || 0);
    });
    return m;
  }, [store.leads]);

  // O mapeador grava `alertasPend: 0` em todo cliente; a contagem real sai dos alertas da agenda, com a regra da página do cliente
  // (cliente-page.jsx): pendente e do cliente direto OU de um negócio dele. Um passo só, porque são milhares de clientes e um
  // `filter` de alertas por cliente faria clientes x alertas comparações a cada render.
  const alertasPendPorCliente = useMemoCli(() => {
    const clienteDoNegocio = new Map();
    store.leads.forEach(l => { if (l.clienteId) clienteDoNegocio.set(l.id, l.clienteId); });
    const m = new Map();
    const soma = (clienteId) => m.set(clienteId, (m.get(clienteId) || 0) + 1);
    store.alerts.forEach(a => {
      if (a.feito) return;
      const doNegocio = a.leadId != null ? clienteDoNegocio.get(a.leadId) : undefined;
      if (a.clienteId) soma(a.clienteId);
      // Alerta com cliente E negócio do mesmo cliente conta uma vez só.
      if (doNegocio && doNegocio !== a.clienteId) soma(doNegocio);
    });
    return m;
  }, [store.alerts, store.leads]);

  // Escopo: roles "own" só veem clientes vinculados aos seus leads
  const scopedClientes = useMemoCli(() => {
    const papel = (store.me?.papel ?? 'admin').toLowerCase();
    const scope = (ROLE_SCOPE && ROLE_SCOPE[papel]) ?? 'team';
    if (scope === 'team') return store.clientes;
    const myLeadClienteIds = new Set(
      store.leads.filter(l => l.ownerId === store.me?.id).map(l => l.clienteId).filter(Boolean)
    );
    // O cliente que a pessoa cadastrou ainda nao tem negocio (0086, `criado_por`).
    return store.clientes.filter(c => myLeadClienteIds.has(c.id) || (c.criadoPor != null && c.criadoPor === store.me?.id));
  }, [store.clientes, store.leads, store.me]);

  const filtered = useMemoCli(() => {
    return scopedClientes.filter(c => {
      if (filters.tipo.length && !filters.tipo.includes(c.tipo)) return false;
      if (filters.status.length && !filters.status.includes(c.status)) return false;
      if (filters.canal.length && !filters.canal.includes(c.canalId)) return false;
      if (filters.nicho.length && !filters.nicho.includes(c.nicho)) return false;
      if (filters.uf.length && !filters.uf.includes(c.endereco?.uf || "")) return false;
      if (filters.alertas && !((alertasPendPorCliente.get(c.id) || 0) > 0)) return false;
      if (filters.reativar && !c.reativacao) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const canal = c.canalId ? store.especificadores.find(e => e.id === c.canalId) : null;
        const hay = (c.nome + " " + c.cidade + " " + c.contato + " " + (canal?.nome || "")).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [scopedClientes, filters, store.especificadores, alertasPendPorCliente]);

  // Clientes em risco: ativos sem compra nos últimos 90 dias
  const emRiscoSet = useMemoCli(() => {
    const noventa = 90;
    const ids = new Set();
    filtered.forEach(c => {
      if (c.isCanal || c.status !== "ativo") return;
      if (c.lastBuyDias == null || c.lastBuyDias >= noventa) ids.add(c.id);
    });
    return ids;
  }, [filtered]);

  // métricas topo
  const byTipo = useMemoCli(() => {
    const m = Object.fromEntries(TIPO_CLIENTE.map(t => [t.id, 0]));
    scopedClientes.forEach(c => m[c.tipo]++);
    return m;
  }, [scopedClientes]);

  const totFaturado = filtered.reduce((s, c) => s + c.faturamento, 0);
  const totAndamento = filtered.reduce((s, c) => s + (pipelineByCliente[c.id] || 0), 0);
  const totAtivos = filtered.filter(c => c.status === "ativo").length;
  const totReativar = filtered.filter(c => c.reativacao).length;

  return (
    <div className="main main-scroll clientes-page" data-screen-label="Clientes">
      <ClientesFiltros filters={filters} setFilters={setFilters} total={filtered.length} onNewCliente={onNewCliente} clientes={scopedClientes} canais={store.especificadores} pipelineByCanal={pipelineByCanal} />

      {/* Métricas + filtro canal */}
      <div className="clientes-strip">
        <div className="strip-stats">
          <div><span className="lbl">Em exibição</span><span className="val mono">{filtered.length}</span></div>
          <div><span className="lbl">Ativos</span><span className="val mono ok">{totAtivos}</span></div>
          <div><span className="lbl">Faturado · janela</span><span className="val mono">{brlCompact(totFaturado)}</span></div>
          <div><span className="lbl">Em andamento · pipeline</span><span className="val mono accent">{brlCompact(totAndamento)}</span></div>
          {/* Some quando não há indicação em exibição: a faixa de 4 números segue como era. No celular ele fica escondido (`.strip-reativar`): com ele a faixa ganha uma 3ª linha e a 1ª linha de cliente passa dos 40 % da janela (medido em 29/10: 40,9 % a 375x812 e 44,8 % a 360x740). */}
          {totReativar > 0 && <div className="strip-reativar"><span className="lbl">Para reativar</span><span className="val mono warn">{totReativar}</span></div>}
          <div className="strip-divider" />
          {TIPO_CLIENTE.map(t => (
            <div key={t.id} className="strip-tipo">
              <span className="dot" style={{ background: t.color }} />
              <span className="lbl">{t.short}</span>
              <span className="val mono">{byTipo[t.id] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {filters.__canalOpen && (
        <div className="fb-mob-painel" style={{ padding: "10px 22px", background: "var(--color-surface)", borderBottom: "1px solid var(--color-border)", display: "flex", flexWrap: "wrap", gap: 6 }}>
          <button className={`chip-btn ${filters.canal.length === 0 ? "active" : ""}`} onClick={() => setFilters(f => ({ ...f, canal: [] }))}>Todos os canais</button>
          {store.especificadores.map(e => {
            const on = filters.canal.includes(e.id);
            return (
              <button key={e.id} className={`chip-btn ${on ? "active" : ""}`} onClick={() => setFilters(f => ({ ...f, canal: on ? f.canal.filter(x => x !== e.id) : [...f.canal, e.id] }))}>
                {on && <Icon name="check" size={11} />}
                {e.nome}
                <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)", marginLeft: 4 }}>{e.clientesCount}</span>
                {(pipelineByCanal[e.id] || 0) > 0 && (
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, fontWeight: 700, color: "var(--color-accent-deep)", marginLeft: 6 }}>{brlCompact(pipelineByCanal[e.id])} em and.</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="clientes-lista" style={{ padding: "16px 22px" }}>
        <div className="card-section">
          <table className="data-table clientes-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Canal / Especificador</th>
                <th>Faturamento</th>
                <th>Negócios</th>
                <th>Alertas</th>
                <th style={{ textAlign: "right" }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, limite).map(c => (
                <ClienteRow key={c.id} cliente={c} especificadores={store.especificadores} onOpen={onOpenCliente} pipeline={pipelineByCliente[c.id] || 0} alertasPend={alertasPendPorCliente.get(c.id) || 0} emRisco={emRiscoSet.has(c.id)} />
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="8">
                  <div className="empty" style={{ padding: 60 }}>
                    <Icon name="users" size={28} style={{ color: "var(--color-ink-faint)", marginBottom: 8 }} />
                    <div style={{ fontWeight: 600 }}>Nenhum cliente encontrado</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Ajuste os filtros ou crie um novo cliente.</div>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > POR_VEZ && (
          <div className="clientes-mais" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "14px 0", fontSize: 12, color: "var(--color-ink-soft)" }}>
            <span>Mostrando {Math.min(limite, filtered.length)} de {filtered.length} clientes</span>
            {limite < filtered.length && <button className="btn" onClick={() => setLimite(l => l + POR_VEZ)}>Mostrar mais (+{POR_VEZ})</button>}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Cliente Drawer (ficha cadastral + timeline + tabela + negócios) =====

function whatsappLink(telefone) {
  return waLink(telefone);
}

function TabClienteFicha({ cliente, especificadores, onExpand, onEdit }) {
  const canal = cliente.canalId ? especificadores.find(e => e.id === cliente.canalId) : null;
  const tipoBadge = TIPO_CLIENTE_BY_ID[cliente.tipo];
  const nicho = cliente.nicho ? NICHOS_BY_ID[cliente.nicho] : null;
  const wpp = whatsappLink(cliente.telefone);
  const docOk = fiscalOk(cliente);
  const contatos = (cliente.contatos && cliente.contatos.length > 0) ? cliente.contatos : [{ id: "ct_fallback", nome: cliente.contato, cargo: cliente.cargo, telefone: cliente.telefone, email: cliente.email, principal: true }];
  return (
    <div>
      <div className="ficha-strip">
        <div className="ficha-stat"><div className="lbl">Faturamento</div><div className="val mono">{brlCompact(cliente.faturamento)}</div></div>
        <div className="ficha-stat"><div className="lbl">Últ. compra</div><div className="val">{cliente.lastBuy}</div></div>
        <div className="ficha-stat"><div className="lbl">Negócios</div><div className="val mono">{cliente.dealsAtivos} ativos / {cliente.dealsTotal}</div></div>
        <div className="ficha-stat"><div className="lbl">Desde</div><div className="val">{dataBr(cliente.criadoEm)}</div></div>
      </div>

      <div className="field">
        <label>Razão social / nome</label>
        <div className="control"><input readOnly value={cliente.nome} /></div>
      </div>
      {nicho && <div className="field"><label>Nicho de atuação</label><div className="control"><Icon name={nicho.icon} size={13} style={{ color: "var(--color-accent-deep)", marginRight: 8 }} /><input readOnly value={nicho.label} /></div></div>}

      {/* Contatos (múltiplos) */}
      <div className="section-h" style={{ marginTop: 14 }}>Contatos <span>· {contatos.length}</span></div>
      <div className="contato-list">
        {contatos.map(ct => {
          const w = whatsappLink(ct.telefone);
          // Campo vazio não entra na linha: "— · — · e-mail" parecia dado perdido, e o ERP traz muito contato só com e-mail.
          const partes = [
            ct.cargo && <span key="cargo">{ct.cargo}</span>,
            ct.telefone && <span key="telefone" className="mono">{ct.telefone}</span>,
            ct.email && <span key="email">{ct.email}</span>,
          ].filter(Boolean);
          return (
            <div key={ct.id} className="contato-row">
              <div className="ct-av">{(ct.nome || "—").split(" ").filter(Boolean).map(p => p[0]).slice(0, 2).join("").toUpperCase()}</div>
              <div className="ct-body">
                <div className="nm">{ct.nome || "Sem nome"}{ct.principal && <span className="ct-main">principal</span>}</div>
                <div className="meta">{partes.length === 0 ? "Sem dados de contato" : partes.map((p, i) => <React.Fragment key={p.key}>{i > 0 && " · "}{p}</React.Fragment>)}</div>
              </div>
              {w && <a href={w} target="_blank" rel="noreferrer" className="wpp-btn" title="Abrir WhatsApp Web"><Icon name="message-square" size={13} /></a>}
            </div>
          );
        })}
        <button className="contato-add" onClick={onEdit}><Icon name="plus" size={12} /> Adicionar contato</button>
      </div>

      {/* Bloco fiscal */}
      <div className="section-h" style={{ marginTop: 14 }}>Cadastro fiscal {docOk ? <span className="doc-valid ok"><Icon name="check" size={10} /> válido</span> : <span className="doc-valid err"><Icon name="alert-triangle" size={10} /> verificar</span>}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div className="field"><label>Tipo</label><div className="control"><input readOnly value={cliente.tipoDoc === "cnpj" ? "Pessoa Jurídica" : "Pessoa Física"} /></div></div>
        <div className="field"><label>{cliente.tipoDoc === "cnpj" ? "CNPJ" : "CPF"}</label><div className="control mono"><input readOnly value={cliente.tipoDoc === "cnpj" ? cliente.cnpj : cliente.cpf} /></div></div>
        {cliente.tipoDoc === "cnpj" && <div className="field"><label>Inscrição Estadual</label><div className="control mono"><input readOnly value={cliente.ie || "isento"} /></div></div>}
      </div>
      {!docOk && (
        <div className="fiscal-warn">
          <Icon name="alert-triangle" size={13} />
          <span>Cadastro fiscal incompleto — <strong>obrigatório para gerar propostas</strong>.</span>
          <button className="btn sm" onClick={onEdit}>Completar</button>
        </div>
      )}

      {/* Dados para faturamento */}
      <div className="section-h" style={{ marginTop: 14 }}>Dados para faturamento</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div className="field"><label>E-mail de faturamento · NF-e</label><div className="control"><Icon name="mail" size={13} style={{ color: "var(--color-ink-soft)", marginRight: 8 }} /><input readOnly value={cliente.emailFinanceiro || cliente.email || "—"} /></div></div>
        <div className="field"><label>Condição de pagamento</label><div className="control mono"><input readOnly value={cliente.condPagamento || "—"} /></div></div>
      </div>

      {/* Endereço */}
      <div className="section-h" style={{ marginTop: 14 }}>Endereço de faturamento</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div className="field"><label>CEP</label><div className="control mono"><input readOnly value={cliente.endereco.cep} /></div></div>
        <div className="field"><label>Bairro</label><div className="control"><input readOnly value={cliente.endereco.bairro} /></div></div>
      </div>
      <div className="field"><label>Logradouro</label><div className="control"><input readOnly value={`${cliente.endereco.rua}, ${cliente.endereco.num}${cliente.endereco.complemento ? " · " + cliente.endereco.complemento : ""}`} /></div></div>
      <div className="field"><label>Cidade / UF</label><div className="control"><Icon name="globe" size={13} style={{ color: "var(--color-ink-soft)", marginRight: 8 }} /><input readOnly value={`${cliente.endereco.cidade} · ${cliente.endereco.uf}`} /></div></div>

      {!cliente.enderecoEntregaMesmo && cliente.enderecoEntrega && (
        <>
          <div className="section-h" style={{ marginTop: 14 }}>Endereço de entrega <span>(diferente)</span></div>
          <div className="field"><label>Endereço</label><div className="control"><input readOnly value={`${cliente.enderecoEntrega.rua}, ${cliente.enderecoEntrega.num} · ${cliente.enderecoEntrega.bairro} · ${cliente.enderecoEntrega.cidade}/${cliente.enderecoEntrega.uf}`} /></div></div>
        </>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
        <div className="field"><label>Tipo de cliente</label><div className="control" style={{ display: "flex", alignItems: "center", padding: "0 12px" }}><TipoChip tipo={cliente.tipo} /></div></div>
        <div className="field"><label>Status carteira</label><div className="control control-status"><StatusCarteira status={cliente.status} reativacao={cliente.reativacao} /></div></div>
      </div>

      <div className="field">
        <label>Canal / Especificador</label>
        {canal ? (
          <div className="canal-card">
            <div className="canal-card-ic"><Icon name="compass" size={16} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="nm">{canal.nome}</div>
              <div className="meta">{canal.tipo} · {canal.cidade} · responsável: {canal.responsavel}</div>
            </div>
            <div className="canal-card-tag mono">{canal.pctRepasse}% repasse</div>
          </div>
        ) : (
          <div className="control" style={{ background: "var(--color-bg)" }}>
            <Icon name="compass" size={13} style={{ color: "var(--color-ink-faint)", marginRight: 8 }} />
            <span style={{ color: "var(--color-ink-faint)", fontSize: 13 }}>Cliente direto · sem canal vinculado</span>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <button className="btn primary" onClick={onEdit}><Icon name="pencil" size={13} /> Editar ficha</button>
        <button className="btn"><Icon name="plus" size={13} /> Novo negócio</button>
        {wpp && (
          <a href={wpp} target="_blank" rel="noreferrer" className="btn" style={{ textDecoration: "none" }}>
            <Icon name="message-square" size={13} /> WhatsApp Web
          </a>
        )}
        {onExpand && <button className="btn ghost" style={{ marginLeft: "auto" }} onClick={onExpand}><Icon name="maximize" size={13} /> Perfil completo</button>}
      </div>
    </div>
  );
}

function TabClienteTabela({ cliente }) {
  const store = useStore();
  const tabela = store.getTabelaById(cliente.tabelaId);
  if (!tabela) return <div className="empty" style={{ padding: 30 }}>Nenhuma tabela de preços associada.</div>;
  return (
    <div>
      <div className="tabela-head" style={{ borderColor: tabela.cor }}>
        <div className="th-icon" style={{ background: tabela.cor }}><Icon name="file-spreadsheet" size={15} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="th-nm">{tabela.nome}</div>
          <div className="th-meta">{tabela.descricao}</div>
        </div>
        <div className="th-desc mono">{rotuloDaTabela(tabela)}</div>
      </div>
      <ListaDePrecos tabela={tabela} />
      <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--color-ink-soft)" }}>
        <Icon name="info" size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
        Tabela aplicada automaticamente em novos orçamentos para este cliente.
      </div>
    </div>
  );
}

function TabClienteNegocios({ cliente, onOpenLead }) {
  const store = useStore();
  const [view, setView] = useStateCli("all");
  const negocios = store.getLeadsByCliente(cliente.id);
  if (negocios.length === 0) return <div className="empty" style={{ padding: 30 }}><Icon name="layout-kanban" size={24} style={{ color: "var(--color-ink-faint)", marginBottom: 6 }} /><div>Sem negócios para este cliente ainda.</div></div>;
  const ativos = negocios.filter(n => n.stage !== "GANHO" && !n.perdido);
  const ganhos = negocios.filter(n => n.stage === "GANHO" && !n.perdido);
  const perdidos = negocios.filter(n => n.perdido);

  const renderGroup = (label, items, tone) => items.length === 0 ? null : (
    <div className="neg-group">
      <div className={`neg-group-h tone-${tone}`}>{label} · <span className="mono">{items.length}</span></div>
      {items.map(n => (
        <button key={n.id} className="neg-row" onClick={() => onOpenLead(n.id)}>
          <span className={`stage-dot stage-${n.stage}`} />
          <div className="neg-row-body">
            <div className="nm">{n.empresa}</div>
            <div className="meta">{STAGE_NAMES[n.stage]} · {n.proximaAcao}</div>
          </div>
          <div className="neg-val mono">{n.valor > 0 ? brl(n.valor) : "—"}</div>
          {n.motivoPerda && <span className="lost-tag">{n.motivoPerda}</span>}
          <Icon name="chevron-right" size={13} style={{ color: "var(--color-ink-faint)" }} />
        </button>
      ))}
    </div>
  );

  return (
    <div>
      <div className="seg" style={{ marginBottom: 12 }}>
        <button className={view === "all" ? "on" : ""} onClick={() => setView("all")}>Todos <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)" }}>{negocios.length}</span></button>
        <button className={view === "ativos" ? "on" : ""} onClick={() => setView("ativos")}>Em andamento <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)" }}>{ativos.length}</span></button>
        <button className={view === "ganhos" ? "on" : ""} onClick={() => setView("ganhos")}>Ganhos <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)" }}>{ganhos.length}</span></button>
        <button className={view === "perdidos" ? "on" : ""} onClick={() => setView("perdidos")}>Perdidos <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)" }}>{perdidos.length}</span></button>
      </div>
      {(view === "all" || view === "ativos") && renderGroup("Em andamento", ativos, "accent")}
      {(view === "all" || view === "ganhos") && renderGroup("Ganhos", ganhos, "ok")}
      {(view === "all" || view === "perdidos") && renderGroup("Perdidos", perdidos, "danger")}
      {view !== "all" && { ativos, ganhos, perdidos }[view]?.length === 0 && <div className="empty" style={{ padding: 24 }}>Nada neste grupo.</div>}
    </div>
  );
}

function TabClienteTimeline({ cliente }) {
  const store = useStore();
  // Build a synthesized timeline from gmail + alerts + leads activity
  const events = useMemoCli(() => {
    const out = [];
    const threads = GMAIL_THREADS[cliente.id] || [];
    threads.forEach(t => {
      out.push({
        tipo: "email",
        autor: t.direction === "in" ? t.from : "Você → " + t.to,
        autorTipo: "humano",
        texto: <><strong>{t.subject}</strong> · {t.snippet}</>,
        detail: "Gmail · sincronizado automaticamente",
        when: t.when,
      });
    });
    // alerts vinculados
    store.alerts.filter(a => a.clienteId === cliente.id || store.getLeadsByCliente(cliente.id).some(l => l.id === a.leadId)).forEach(a => {
      out.push({
        tipo: "alert", autor: store.users.find(u => u.id === a.autor)?.nome || "Time", autorTipo: "humano",
        texto: <>Alerta: <strong>{a.tipo}</strong> · {a.quando}</>,
        when: a.feito ? "feito" : (a.vencido ? "vencido" : "agendado"),
      });
    });
    // leads
    store.getLeadsByCliente(cliente.id).forEach(l => {
      out.push({
        tipo: "stage", autor: store.users.find(u => u.id === l.ownerId)?.nome || "Time", autorTipo: "humano",
        texto: <>Negócio <strong>{l.empresa}</strong> está em <strong>{STAGE_NAMES[l.stage]}</strong>.</>,
        when: `há ${l.diasNoEstagio}d`,
      });
    });
    return out;
  }, [cliente, store.alerts, store.leads]);

  const iconForType = (t) => ({ stage: "git-merge", criacao: "sparkles", field: "edit", note: "message-square", alert: "bell", bant: "shield-check", email: "mail" }[t] || "circle");
  const wrapClsForType = (t) => t === "email" ? "email" : (t === "stage" ? "stage" : (t === "alert" ? "alert" : ""));

  return (
    <div>
      <div className="seg" style={{ marginBottom: 12 }}>
        <button className="on">Tudo <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)" }}>{events.length}</span></button>
        <button><Icon name="mail" size={11} /> Gmail</button>
        <button><Icon name="bell" size={11} /> Alertas</button>
      </div>
      <div className="timeline">
        {events.map((e, i) => (
          <div key={i} className="tl-item">
            <div className={`tl-icon ${wrapClsForType(e.tipo)}`}><Icon name={iconForType(e.tipo)} size={13} strokeWidth={2} /></div>
            <div className="tl-body">
              <div className="tl-head">
                <span className="tl-author">{e.autor}</span>
                <span className="tl-time">{e.when}</span>
              </div>
              <div className="tl-text">{e.texto}</div>
              {e.detail && <pre className="tl-detail">{e.detail}</pre>}
            </div>
          </div>
        ))}
        {events.length === 0 && <div className="empty">Sem eventos registrados.</div>}
      </div>
    </div>
  );
}

function TabClienteCurva({ cliente }) {
  const curva = cliente.curvaConsumo || [];
  const recomendacoes = recomendacaoParaCliente(cliente);
  const nicho = cliente.nicho ? NICHOS_BY_ID[cliente.nicho] : null;
  const max = curva[0]?.valorTotal || 1;
  return (
    <div>
      <div className="section-h">Curva de consumo <span>· produtos que este cliente mais compra</span></div>
      {curva.length === 0 && (
        <div className="empty" style={{ padding: 24 }}>
          <Icon name="package" size={22} style={{ color: "var(--color-ink-faint)", marginBottom: 6 }} />
          <div>Cliente em prospecção — ainda sem histórico de compra.</div>
        </div>
      )}
      <div className="curva-list">
        {curva.map((c, i) => (
          <div key={i} className="curva-row">
            <div className="cv-rank mono">{i + 1}</div>
            <div className="cv-info">
              <div className="nm">{c.nome}</div>
              <div className="meta">{c.categoria} · {c.qtdTotal} unid. · última compra {c.ultimaCompra}</div>
            </div>
            <div className="cv-bar"><div className="fill" style={{ width: Math.round((c.valorTotal / max) * 100) + "%" }} /></div>
            <div className="cv-val mono">{brlCompact(c.valorTotal)}</div>
          </div>
        ))}
      </div>

      {nicho && recomendacoes.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div className="section-h">Sugestões por nicho <span>· baseado em {nicho.label}</span></div>
          <div className="reco-grid">
            {recomendacoes.map(p => (
              <div key={p.id} className="reco-card">
                <div className="rc-ic"><Icon name="sparkles" size={13} /></div>
                <div className="rc-body">
                  <div className="nm">{p.nome}</div>
                  <div className="meta">{p.categoria} · giro {p.giro}</div>
                </div>
                <div className="rc-price mono">{brl(p.precoLista)}</div>
              </div>
            ))}
          </div>
          <div className="reco-note">
            <Icon name="info" size={11} />
            Motor de recomendação baseado no nicho de atuação cadastrado. Foco: {nicho.focus}.
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Aba Alertas do cliente (lista pendentes + concluir + criar novo) =====
function TabClienteAlertas({ cliente, onCreateAlert, onOpenLead }) {
  const store = useStore();
  const negocios = store.getLeadsByCliente(cliente.id);
  const all = store.alerts.filter(a => a.clienteId === cliente.id || negocios.some(n => n.id === a.leadId));
  const pend = all.filter(a => !a.feito);
  const feitos = all.filter(a => a.feito);

  const renderAlert = (a) => {
    const lead = a.leadId ? store.leads.find(l => l.id === a.leadId) : null;
    return (
      <div key={a.id} className={`cli-alerta ${a.feito ? "done" : ""}`}>
        <button className="al-check" onClick={() => store.toggleAlertDone(a.id)} title={a.feito ? "Reabrir alerta" : "Marcar como feito"}>
          <Icon name={a.feito ? "check-circle-2" : "circle"} size={16} />
        </button>
        <div className="al-body">
          <div className="nm">{a.tipo}{a.vencido && !a.feito && <span className="al-late">vencido</span>}</div>
          <div className="meta">
            {a.quando} · {a.canal}
            {lead && <> · <button className="al-lead" onClick={() => onOpenLead(lead.id)}>{lead.empresa}</button></>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div className="section-h" style={{ margin: 0, flex: 1 }}>Pendentes <span>· {pend.length}</span></div>
        <button className="btn primary sm" onClick={onCreateAlert}><Icon name="plus" size={12} /> Novo alerta</button>
      </div>
      <div className="cli-alerta-list">
        {pend.length === 0 && <div className="empty" style={{ padding: 20 }}>Nenhum alerta pendente para este cliente.</div>}
        {pend.map(renderAlert)}
      </div>
      {feitos.length > 0 && (
        <>
          <div className="section-h" style={{ marginTop: 16 }}>Concluídos <span>· {feitos.length}</span></div>
          <div className="cli-alerta-list">{feitos.map(renderAlert)}</div>
        </>
      )}
    </div>
  );
}

function ClienteDrawer({ clienteId, onClose, onOpenLead, onCreateAlert, onExpandToPage, onEdit }) {
  const store = useStore();
  const [tab, setTab] = useStateCli("ficha");
  const cliente = store.getClienteById(clienteId);
  if (!cliente) return null;
  const tipo = TIPO_CLIENTE_BY_ID[cliente.tipo];
  const negocios = store.getLeadsByCliente(cliente.id);
  const alertasCount = store.alerts.filter(a => a.clienteId === cliente.id || negocios.some(n => n.id === a.leadId)).filter(a => !a.feito).length;

  const tabs = [
    { id: "ficha",     label: "Ficha",    icon: "user" },
    { id: "negocios",  label: "Negócios", icon: "layout-kanban", pill: negocios.length },
    { id: "alertas",   label: "Alertas",  icon: "bell", pill: alertasCount },
    { id: "tabela",    label: "Tabela",   icon: "file-spreadsheet" },
    { id: "curva",     label: "Curva",    icon: "package" },
    { id: "timeline",  label: "Timeline", icon: "history" },
  ];

  return (
    <aside className="drawer drawer-cli" data-screen-label={`Cliente Drawer / ${cliente.nome}`}>
      <div className="drawer-head" style={{ background: `linear-gradient(180deg, ${tipo?.tone || "var(--color-bg)"} 0%, var(--color-surface) 100%)` }}>
        <div className="top">
          <ClienteAvatar cliente={cliente} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="title">{cliente.nome}</div>
            <div className="meta">
              <TipoChip tipo={cliente.tipo} size="sm" />
              <span>·</span>
              <StatusCarteira status={cliente.status} reativacao={cliente.reativacao} />
              <span>·</span>
              <span>{cliente.cidade}</span>
            </div>
          </div>
          <button className="icon-btn" onClick={onExpandToPage} title="Abrir perfil completo" style={{ marginRight: 4 }}><Icon name="maximize" size={15} /></button>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        {alertasCount > 0 && (
          <button className="cli-alert-row" onClick={() => setTab("alertas")} title="Ver alertas pendentes">
            <Icon name="bell" size={13} />
            <strong>{alertasCount}</strong> {alertasCount === 1 ? "alerta pendente" : "alertas pendentes"} neste cliente
            <span style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 600, textDecoration: "underline" }}>ver alertas</span>
            <span
              role="button"
              className="cli-alert-new"
              onClick={(e) => { e.stopPropagation(); onCreateAlert(); }}
              title="Criar novo alerta"
            >+ novo</span>
          </button>
        )}
      </div>
      <div className="drawer-tabs">
        {tabs.map(t => (
          <button key={t.id} className={`drawer-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            <Icon name={t.icon} size={13} />
            {t.label}
            {t.pill > 0 && <span className="pill">{t.pill}</span>}
          </button>
        ))}
      </div>
      <div className="drawer-body">
        {tab === "ficha"    && <TabClienteFicha    cliente={cliente} especificadores={store.especificadores} onExpand={onExpandToPage} onEdit={onEdit} />}
        {tab === "negocios" && <TabClienteNegocios cliente={cliente} onOpenLead={onOpenLead} />}
        {tab === "alertas"  && <TabClienteAlertas  cliente={cliente} onCreateAlert={onCreateAlert} onOpenLead={onOpenLead} />}
        {tab === "tabela"   && <TabClienteTabela   cliente={cliente} />}
        {tab === "curva"    && <TabClienteCurva    cliente={cliente} />}
        {tab === "timeline" && <TabClienteTimeline cliente={cliente} />}
      </div>
    </aside>
  );
}

export {
  ClientesPage, ClienteDrawer, ClienteAvatar, TipoChip, StatusCarteira,
  TabClienteFicha, TabClienteCurva, TabClienteTabela, TabClienteNegocios,
  TabClienteAlertas, TabClienteTimeline, whatsappLink,
};
